import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './Envase.css';

const formatearFecha = (f) => {
    if (!f) return '';
    const [y, m, d] = String(f).split('-');
    return `${d}/${m}/${y}`;
};

const obtenerFechaHoy = () => new Date().toISOString().split('T')[0];

const formatearNumero = (v, dec = 2) => {
    if (v == null || v === '') return '0,00';
    return Number(v).toLocaleString('es-PY', {
        minimumFractionDigits: dec, maximumFractionDigits: dec
    });
};

const formularioInicial = () => ({
    noperacion: null,
    fecha: obtenerFechaHoy(),
    idOrigen: '',
    idDestino: '',
    idSucursal: '',
    obs: '',
    idOperador: '',
    detalles: []
});

function STransfer({ usuario }) {
    const [transferencias, setTransferencias] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState(10);

    const [modalAbierto, setModalAbierto] = useState(false);
    const [modalVerAbierto, setModalVerAbierto] = useState(false);
    const [transferVer, setTransferVer] = useState(null);
    const [formulario, setFormulario] = useState(formularioInicial());

    // Catalogos
    const [depositos, setDepositos] = useState([]);
    const [sucursales, setSucursales] = useState([]);

    // Buscador de productos (para agregar al detalle)
    const [busquedaProducto, setBusquedaProducto] = useState('');
    const [productosEncontrados, setProductosEncontrados] = useState([]);
    const [detalleTemp, setDetalleTemp] = useState({ idProducto: '', descripcion: '', cantidad: 1, existencia: 0, costoprom: 0 });

    useEffect(() => { cargar(); cargarCatalogos(); }, []);

    const cargar = async () => {
        try {
            const r = await axios.get('/api/transferencias');
            setTransferencias(r.data);
        } catch (e) {
            console.error('Error al cargar transferencias:', e);
            alert('Error al cargar transferencias');
        }
    };

    const cargarCatalogos = async () => {
        try {
            const [rDep, rSuc] = await Promise.all([
                axios.get('/api/depositos'),
                axios.get('/api/sucursales')
            ]);
            setDepositos(rDep.data);
            setSucursales(rSuc.data);
        } catch (e) {
            console.error('Error catalogos:', e);
        }
    };

    // Filtrado
    const filtradas = transferencias.filter(t => {
        if (fechaDesde && (!t.fecha || t.fecha < fechaDesde)) return false;
        if (fechaHasta && (!t.fecha || t.fecha > fechaHasta)) return false;
        if (busqueda) {
            const q = busqueda.toLowerCase();
            const matches =
                t.noperacion?.toString().includes(busqueda) ||
                (t.obs || '').toLowerCase().includes(q) ||
                (t.idOperador || '').toLowerCase().includes(q);
            if (!matches) return false;
        }
        return true;
    });

    const totalPaginas = Math.max(1, Math.ceil(filtradas.length / porPagina));
    const inicio = (pagina - 1) * porPagina;
    const paginadas = filtradas.slice(inicio, inicio + porPagina);

    // === Alta ===
    const abrirModalNuevo = () => {
        setFormulario({ ...formularioInicial(), idOperador: usuario || '' });
        setBusquedaProducto('');
        setProductosEncontrados([]);
        setDetalleTemp({ idProducto: '', descripcion: '', cantidad: 1, existencia: 0, costoprom: 0 });
        setModalAbierto(true);
    };

    const cerrarModal = () => setModalAbierto(false);

    const seleccionarOrigen = (id) => {
        setFormulario(f => ({ ...f, idOrigen: id }));
        // Al cambiar el origen, se resetea el buscador y detalles agregados (el stock cambia por deposito)
        setBusquedaProducto('');
        setProductosEncontrados([]);
        setDetalleTemp({ idProducto: '', descripcion: '', cantidad: 1, existencia: 0, costoprom: 0 });
    };

    const busquedaProductoRef = useRef({ timer: null, secuencia: 0 });
    const buscarProductosAPI = (texto) => {
        setBusquedaProducto(texto);
        if (!formulario.idOrigen) {
            alert('Debe seleccionar primero el deposito de origen');
            return;
        }
        // 250 ms tras la ultima tecla; solo se muestra la respuesta de la ultima busqueda
        const ref = busquedaProductoRef.current;
        clearTimeout(ref.timer);
        if (texto.trim().length < 2) { ref.secuencia++; setProductosEncontrados([]); return; }
        const seq = ++ref.secuencia;
        ref.timer = setTimeout(async () => {
            try {
                const r = await axios.get('/api/transferencias/buscar-producto', {
                    params: { busqueda: texto, idOrigen: formulario.idOrigen }
                });
                if (seq === ref.secuencia) setProductosEncontrados(r.data);
            } catch (e) { console.error(e); }
        }, 250);
    };

    const seleccionarProducto = (p) => {
        setDetalleTemp({
            idProducto: p.id_producto,
            descripcion: p.descripcion,
            cantidad: 1,
            existencia: Number(p.existencia_origen) || 0,
            costoprom: Number(p.costoprom) || 0
        });
        setBusquedaProducto(p.descripcion);
        setProductosEncontrados([]);
    };

    const agregarDetalle = () => {
        if (!detalleTemp.idProducto) { alert('Seleccione un producto'); return; }
        if (detalleTemp.cantidad <= 0) { alert('La cantidad debe ser mayor a 0'); return; }
        if (detalleTemp.cantidad > detalleTemp.existencia) {
            if (!window.confirm(`Cantidad (${detalleTemp.cantidad}) excede la existencia en origen (${detalleTemp.existencia}).\nEl trigger rechazara la operacion si queda negativo. ¿Continuar?`)) return;
        }
        // No permitir duplicados (PK compuesta noperacion+id_producto)
        if (formulario.detalles.some(d => d.idProducto === detalleTemp.idProducto)) {
            alert('El producto ya esta en el detalle');
            return;
        }
        setFormulario(f => ({
            ...f,
            detalles: [...f.detalles, {
                idProducto: detalleTemp.idProducto,
                nomProducto: detalleTemp.descripcion,
                cantidad: Number(detalleTemp.cantidad),
                costoprom: Number(detalleTemp.costoprom) || 0,
                monto: Number(detalleTemp.costoprom) * Number(detalleTemp.cantidad)
            }]
        }));
        setDetalleTemp({ idProducto: '', descripcion: '', cantidad: 1, existencia: 0, costoprom: 0 });
        setBusquedaProducto('');
    };

    const quitarDetalle = (idProducto) => {
        setFormulario(f => ({ ...f, detalles: f.detalles.filter(d => d.idProducto !== idProducto) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formulario.idOrigen) { alert('Seleccione deposito de origen'); return; }
        if (!formulario.idDestino) { alert('Seleccione deposito de destino'); return; }
        if (String(formulario.idOrigen) === String(formulario.idDestino)) {
            alert('El deposito de origen y destino deben ser distintos'); return;
        }
        if (formulario.detalles.length === 0) { alert('Agregue al menos un producto'); return; }

        const payload = {
            fecha: formulario.fecha,
            idOrigen: Number(formulario.idOrigen),
            idDestino: Number(formulario.idDestino),
            idSucursal: formulario.idSucursal ? Number(formulario.idSucursal) : null,
            obs: formulario.obs || '',
            idOperador: formulario.idOperador || usuario || '',
            detalles: formulario.detalles.map(d => ({
                idProducto: d.idProducto,
                cantidad: d.cantidad,
                costoprom: d.costoprom,
                monto: d.monto
            }))
        };

        try {
            await axios.post('/api/transferencias', payload);
            alert('Transferencia registrada correctamente');
            cerrarModal();
            cargar();
        } catch (error) {
            const msg = error.response?.data?.message || 'Error al guardar';
            alert(msg);
        }
    };

    // === Ver ===
    const verTransfer = async (t) => {
        try {
            const r = await axios.get(`/api/transferencias/${t.noperacion}`);
            setTransferVer(r.data);
        } catch (e) {
            console.error(e);
            setTransferVer(t);
        }
        setModalVerAbierto(true);
    };

    // === Eliminar ===
    const eliminar = async (t) => {
        if (!window.confirm(`Eliminar la transferencia #${t.noperacion}?\n\nEl stock volvera al deposito de origen y se descontara del destino automaticamente.`)) return;
        try {
            await axios.delete(`/api/transferencias/${t.noperacion}`);
            alert('Transferencia eliminada. Stock revertido.');
            cargar();
        } catch (error) {
            const msg = error.response?.data?.message || 'Error al eliminar';
            alert(msg);
        }
    };

    // === Excel ===
    const exportarExcel = () => {
        if (filtradas.length === 0) { alert('No hay datos para exportar'); return; }
        const datos = filtradas.map(t => ({
            'N° Operacion': t.noperacion,
            'Fecha': formatearFecha(t.fecha),
            'Origen': depositoNombre(t.idOrigen),
            'Destino': depositoNombre(t.idDestino),
            'Sucursal': t.idSucursal,
            'Monto Total': Number(t.monTotal || 0),
            'Operador': t.idOperador,
            'Observaciones': t.obs
        }));
        const ws = XLSX.utils.json_to_sheet(datos);
        ws['!cols'] = Object.keys(datos[0]).map(k => ({ wch: Math.max(k.length, 14) }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Transferencias');
        XLSX.writeFile(wb, `transferencias_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    // Helpers
    const depositoNombre = (id) => {
        const d = depositos.find(x => Number(x.idDeposito) === Number(id));
        return d ? `${d.idDeposito} - ${d.nomDepto}` : id || '';
    };

    return (
        <div className="envase-container">
            <div className="header-seccion">
                <h2>Transferencia entre Almacenes</h2>
                <div className="acciones-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #ddd', borderRadius: '5px', padding: '4px 8px', backgroundColor: '#fff' }}>
                        <label style={{ fontSize: '12px', color: '#555' }}>📅 Desde</label>
                        <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
                            style={{ padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }} />
                        <label style={{ fontSize: '12px', color: '#555' }}>Hasta</label>
                        <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
                            style={{ padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }} />
                        {(fechaDesde || fechaHasta) && (
                            <button type="button" onClick={() => { setFechaDesde(''); setFechaHasta(''); }}
                                style={{ padding: '2px 6px', border: 'none', backgroundColor: 'transparent', color: '#d97706', cursor: 'pointer' }}>✕</button>
                        )}
                    </div>
                    <input type="text" placeholder="Buscar..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="input-busqueda" />
                    <button onClick={abrirModalNuevo} className="btn btn-nuevo">➕ Nueva Transferencia</button>
                    <button onClick={exportarExcel} className="btn btn-exportar">📊 Exportar Excel</button>
                </div>
            </div>

            <div className="tabla-container">
                <table className="tabla-datos">
                    <thead>
                        <tr>
                            <th>N° Operacion</th>
                            <th>Fecha</th>
                            <th>Origen</th>
                            <th>Destino</th>
                            <th>Sucursal</th>
                            <th style={{ textAlign: 'right' }}>Monto Total</th>
                            <th>Operador</th>
                            <th>Observaciones</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginadas.length === 0 ? (
                            <tr><td colSpan="9" className="sin-datos">No hay transferencias</td></tr>
                        ) : paginadas.map(t => (
                            <tr key={t.noperacion}>
                                <td>{t.noperacion}</td>
                                <td>{formatearFecha(t.fecha)}</td>
                                <td>{depositoNombre(t.idOrigen)}</td>
                                <td>{depositoNombre(t.idDestino)}</td>
                                <td>{t.idSucursal}</td>
                                <td style={{ textAlign: 'right' }}>{formatearNumero(t.monTotal)}</td>
                                <td>{t.idOperador}</td>
                                <td>{t.obs}</td>
                                <td>
                                    <button onClick={() => verTransfer(t)} className="btn-accion" title="Ver detalle">👁</button>
                                    <button onClick={() => eliminar(t)} className="btn-accion" title="Eliminar (revierte stock)"
                                        style={{ color: '#c62828' }}>🗑️</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Paginacion */}
            <div className="paginacion-container">
                <div className="paginacion-info">
                    <span>Mostrando {filtradas.length > 0 ? inicio + 1 : 0} a {Math.min(inicio + porPagina, filtradas.length)} de {filtradas.length}</span>
                    <select value={porPagina} onChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(1); }} className="select-registros">
                        <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                    </select>
                </div>
                <div className="paginacion-controles">
                    <button onClick={() => setPagina(1)} disabled={pagina === 1} className="btn-paginacion">|&lt;</button>
                    <button onClick={() => setPagina(p => p - 1)} disabled={pagina === 1} className="btn-paginacion">&lt;</button>
                    <span className="paginacion-numero">{pagina} / {totalPaginas}</span>
                    <button onClick={() => setPagina(p => p + 1)} disabled={pagina >= totalPaginas} className="btn-paginacion">&gt;</button>
                    <button onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas} className="btn-paginacion">&gt;|</button>
                </div>
            </div>

            {/* Modal Nueva Transferencia */}
            {modalAbierto && (
                <div className="atm-modal-overlay" onClick={cerrarModal}>
                    <div className="atm-modal atm-modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="atm-modal-header">
                            <h3>Nueva Transferencia</h3>
                            <button onClick={cerrarModal} className="atm-modal-close">×</button>
                        </div>

                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                            <div className="atm-modal-body">

                                {/* Cabecera */}
                                <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa' }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: '#001A70', borderBottom: '2px solid #001A70', paddingBottom: '5px' }}>Cabecera</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px' }}>
                                        <div className="form-group">
                                            <label>Fecha *</label>
                                            <input type="date" value={formulario.fecha}
                                                onChange={(e) => setFormulario(f => ({ ...f, fecha: e.target.value }))} required
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Sucursal</label>
                                            <select value={formulario.idSucursal}
                                                onChange={(e) => setFormulario(f => ({ ...f, idSucursal: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                <option value="">-</option>
                                                {sucursales.map(s => <option key={s.idSucursal} value={s.idSucursal}>{s.idSucursal} - {s.nombre}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Deposito ORIGEN *</label>
                                            <select value={formulario.idOrigen}
                                                onChange={(e) => seleccionarOrigen(e.target.value)} required
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                <option value="">-- Seleccione --</option>
                                                {depositos
                                                    .filter(d => !formulario.idSucursal || Number(d.idSucursal) === Number(formulario.idSucursal))
                                                    .map(d => <option key={d.idDeposito} value={d.idDeposito}>{d.idDeposito} - {d.nomDepto}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Deposito DESTINO *</label>
                                            <select value={formulario.idDestino}
                                                onChange={(e) => setFormulario(f => ({ ...f, idDestino: e.target.value }))} required
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                <option value="">-- Seleccione --</option>
                                                {depositos
                                                    .filter(d => (!formulario.idSucursal || Number(d.idSucursal) === Number(formulario.idSucursal)) && String(d.idDeposito) !== String(formulario.idOrigen))
                                                    .map(d => <option key={d.idDeposito} value={d.idDeposito}>{d.idDeposito} - {d.nomDepto}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group" style={{ gridColumn: 'span 4' }}>
                                            <label>Observaciones</label>
                                            <input type="text" value={formulario.obs} maxLength={60}
                                                onChange={(e) => setFormulario(f => ({ ...f, obs: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        </div>
                                    </div>
                                </div>

                                {/* Agregar producto */}
                                {formulario.idOrigen && (
                                    <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#EDF1FB' }}>
                                        <h4 style={{ margin: '0 0 10px 0', color: '#001A70' }}>Agregar producto al detalle</h4>
                                        <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
                                            <div className="form-group" style={{ position: 'relative' }}>
                                                <label>Producto (min 2 caracteres)</label>
                                                <input type="text" placeholder="Buscar por descripcion o codigo..."
                                                    value={busquedaProducto} onChange={(e) => buscarProductosAPI(e.target.value)}
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                                {productosEncontrados.length > 0 && (
                                                    <div style={{
                                                        position: 'absolute', top: '100%', left: 0, right: 0,
                                                        backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '5px',
                                                        maxHeight: '250px', overflowY: 'auto', zIndex: 10,
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                                    }}>
                                                        {productosEncontrados.map(p => (
                                                            <div key={p.id_producto}
                                                                onClick={() => seleccionarProducto(p)}
                                                                style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '13px' }}
                                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f9ff'}
                                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                                                                <strong>{p.id_producto}</strong> - {p.descripcion}
                                                                <span style={{ marginLeft: '8px', color: Number(p.existencia_origen) > 0 ? '#059669' : '#c62828' }}>
                                                                    (stock: {formatearNumero(p.existencia_origen, 2)})
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="form-group">
                                                <label>Existencia origen</label>
                                                <input type="text" value={formatearNumero(detalleTemp.existencia, 2)} readOnly
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px', backgroundColor: '#f0f0f0', textAlign: 'right' }} />
                                            </div>
                                            <div className="form-group">
                                                <label>Cantidad *</label>
                                                <input type="number" value={detalleTemp.cantidad} min="0" step="any"
                                                    onChange={(e) => setDetalleTemp(d => ({ ...d, cantidad: Number(e.target.value) }))}
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px', textAlign: 'right' }} />
                                            </div>
                                            <div className="form-group">
                                                <label>Costo Prom.</label>
                                                <input type="text" value={formatearNumero(detalleTemp.costoprom, 2)} readOnly
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px', backgroundColor: '#f0f0f0', textAlign: 'right' }} />
                                            </div>
                                            <div className="form-group">
                                                <button type="button" onClick={agregarDetalle}
                                                    disabled={!detalleTemp.idProducto}
                                                    className="btn btn-nuevo"
                                                    style={{ marginTop: '5px' }}>+ Agregar</button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Detalle actual */}
                                {formulario.detalles.length > 0 && (
                                    <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa' }}>
                                        <h4 style={{ margin: '0 0 10px 0', color: '#001A70' }}>Detalle ({formulario.detalles.length} productos)</h4>
                                        <table className="tabla-datos">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Producto</th>
                                                    <th style={{ textAlign: 'right' }}>Cantidad</th>
                                                    <th style={{ textAlign: 'right' }}>Costo Prom</th>
                                                    <th style={{ textAlign: 'right' }}>Monto</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formulario.detalles.map(d => (
                                                    <tr key={d.idProducto}>
                                                        <td>{d.idProducto}</td>
                                                        <td>{d.nomProducto}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(d.cantidad, 2)}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(d.costoprom, 2)}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(d.monto, 2)}</td>
                                                        <td><button type="button" onClick={() => quitarDetalle(d.idProducto)} className="btn-accion">🗑</button></td>
                                                    </tr>
                                                ))}
                                                <tr style={{ backgroundColor: '#EDF1FB', fontWeight: 'bold' }}>
                                                    <td colSpan="4" style={{ textAlign: 'right' }}>TOTAL</td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {formatearNumero(formulario.detalles.reduce((a, d) => a + Number(d.monto || 0), 0), 2)}
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className="atm-modal-footer">
                                <button type="button" onClick={cerrarModal} className="atm-btn-cancelar">
                                    Cancelar
                                </button>
                                <button type="submit" className="atm-btn-primary">
                                    Registrar Transferencia
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Ver */}
            {modalVerAbierto && transferVer && (
                <div className="atm-modal-overlay" onClick={() => setModalVerAbierto(false)}>
                    <div className="atm-modal atm-modal-md" onClick={(e) => e.stopPropagation()}>
                        <div className="atm-modal-header">
                            <h3>Transferencia #{transferVer.noperacion}</h3>
                            <button onClick={() => setModalVerAbierto(false)} className="atm-modal-close">×</button>
                        </div>
                        <div className="atm-modal-body">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                                <div><strong>Fecha:</strong> {formatearFecha(transferVer.fecha)}</div>
                                <div><strong>Sucursal:</strong> {transferVer.idSucursal}</div>
                                <div><strong>Origen:</strong> {depositoNombre(transferVer.idOrigen)}</div>
                                <div><strong>Destino:</strong> {depositoNombre(transferVer.idDestino)}</div>
                                <div><strong>Operador:</strong> {transferVer.idOperador}</div>
                                <div><strong>Total:</strong> {formatearNumero(transferVer.monTotal, 2)}</div>
                                <div style={{ gridColumn: 'span 2' }}><strong>Obs:</strong> {transferVer.obs}</div>
                            </div>
                            <h4 style={{ marginBottom: '5px' }}>Detalle</h4>
                            <table className="tabla-datos">
                                <thead>
                                    <tr>
                                        <th>ID</th><th>Producto</th>
                                        <th style={{ textAlign: 'right' }}>Cantidad</th>
                                        <th style={{ textAlign: 'right' }}>Costo Prom</th>
                                        <th style={{ textAlign: 'right' }}>Monto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(transferVer.detalles || []).map(d => (
                                        <tr key={d.idProducto}>
                                            <td>{d.idProducto}</td>
                                            <td>{d.nomProducto}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.cantidad, 2)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.costoprom, 2)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.monto, 2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default STransfer;
